import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { OwnedEntityDto, OwnedEntityRequest, PartyType } from "@/types/parties";
import type { EntityTypeCode } from "@/types/counterparties";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { useDialogs } from "@/hooks/useDialogs";
import { useDeleteOwnedEntity, useUpsertOwnedEntity } from "@/hooks/parties/useParties";

// Party read-flip (WS-A) — the party's owned-entities tree (strict
// one-owner; info-only, never a login). Inline add/edit form; EIN is
// write-only (masked on read — blank keeps the stored value).

const ENTITY_TYPES: EntityTypeCode[] = [
  "LLC", "SCorp", "CCorp", "LP", "LLP", "Trust", "SoleProp", "Other",
];

interface OwnedEntityFormState {
  id?: string;
  legalName: string;
  dba: string;
  entityType: EntityTypeCode;
  stateOfFormation: string;
  dateOfFormation: string;
  ein: string;
  generalNotes: string;
}

const EMPTY_FORM: OwnedEntityFormState = {
  legalName: "",
  dba: "",
  entityType: "LLC",
  stateOfFormation: "",
  dateOfFormation: "",
  ein: "",
  generalNotes: "",
};

export function OwnedEntitiesCard({
  type,
  partyId,
  ownedEntities,
  canEdit,
}: {
  type: PartyType;
  partyId: string;
  ownedEntities: OwnedEntityDto[];
  canEdit: boolean;
}) {
  const toast = useToast();
  const dialogs = useDialogs();
  const [form, setForm] = useState<OwnedEntityFormState | null>(null);
  const [editingEin, setEditingEin] = useState(false);

  const upsert = useUpsertOwnedEntity(type, partyId);
  const remove = useDeleteOwnedEntity(type, partyId);

  const save = () => {
    if (!form) return;
    if (!form.legalName.trim()) {
      toast.error("Legal name is required.");
      return;
    }
    const body: OwnedEntityRequest = {
      id: form.id ?? null,
      legalName: form.legalName,
      dba: form.dba || null,
      entityType: form.entityType,
      stateOfFormation: form.stateOfFormation || null,
      dateOfFormation: form.dateOfFormation || null,
      // FIND-12 for the sensitive field: null keeps the stored ciphertext.
      ein: form.id && !editingEin ? null : form.ein || (form.id ? "" : null),
      generalNotes: form.generalNotes || null,
    };
    upsert.mutate(body, {
      onSuccess: () => {
        toast.success(form.id ? "Owned entity updated." : "Owned entity added.");
        setForm(null);
        setEditingEin(false);
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed."),
    });
  };

  const onDelete = async (e: OwnedEntityDto) => {
    const ok = await dialogs.confirm({
      title: `Remove ${e.legalName}?`,
      message: "The owned entity will no longer appear under this party.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    remove.mutate(e.id, {
      onSuccess: () => toast.success("Owned entity removed."),
      onError: (err) => toast.error(err instanceof Error ? err.message : "Remove failed."),
    });
  };

  return (
    <Card data-testid="party-owned-entities-card">
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Owned entities</CardTitle>
        {canEdit && !form && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setForm(EMPTY_FORM);
              setEditingEin(true);
            }}
          >
            <Plus size={14} aria-hidden /> Add entity
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-space-3">
        {ownedEntities.length === 0 && !form && (
          <p className="text-caption text-gray-mid">No owned entities on file.</p>
        )}

        {ownedEntities.map((e) => (
          <div
            key={e.id}
            className="flex flex-wrap items-center gap-space-2 rounded border border-border-light p-space-3"
            data-testid={`party-owned-entity-${e.id}`}
          >
            <div className="flex flex-col gap-space-1 grow">
              <div className="flex flex-wrap items-center gap-space-2">
                <span className="font-semibold text-onyx">{e.legalName}</span>
                {e.dba && <span className="text-caption text-gray-mid">DBA {e.dba}</span>}
                <span className="text-caption text-gray-mid">{e.entityType}</span>
              </div>
              <span className="text-caption text-gray-mid">
                {[
                  e.stateOfFormation ? `Formed in ${e.stateOfFormation}` : null,
                  e.einMasked ? `EIN ${e.einMasked}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "No formation details"}
              </span>
              {e.generalNotes && (
                <span className="text-caption text-gray-mid">{e.generalNotes}</span>
              )}
            </div>
            {canEdit && (
              <div className="flex items-center gap-space-1">
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Edit ${e.legalName}`}
                  onClick={() => {
                    setForm({
                      id: e.id,
                      legalName: e.legalName,
                      dba: e.dba ?? "",
                      entityType: e.entityType,
                      stateOfFormation: e.stateOfFormation ?? "",
                      dateOfFormation: e.dateOfFormation ?? "",
                      ein: "",
                      generalNotes: e.generalNotes ?? "",
                    });
                    setEditingEin(false);
                  }}
                >
                  <Pencil size={14} aria-hidden />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  aria-label={`Remove ${e.legalName}`}
                  onClick={() => void onDelete(e)}
                  disabled={remove.isPending}
                >
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            )}
          </div>
        ))}

        {form && (
          <div className="flex flex-col gap-space-2 rounded border border-border-light p-space-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
              <div>
                <Label htmlFor="oe-legal-name">Legal name</Label>
                <Input
                  id="oe-legal-name"
                  value={form.legalName}
                  onChange={(e) => setForm({ ...form, legalName: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="oe-dba">DBA</Label>
                <Input
                  id="oe-dba"
                  value={form.dba}
                  onChange={(e) => setForm({ ...form, dba: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="oe-entity-type">Entity type</Label>
                <Select
                  id="oe-entity-type"
                  value={form.entityType}
                  onChange={(e) => setForm({ ...form, entityType: e.target.value as EntityTypeCode })}
                >
                  {ENTITY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="oe-state">State of formation</Label>
                <Input
                  id="oe-state"
                  value={form.stateOfFormation}
                  onChange={(e) => setForm({ ...form, stateOfFormation: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="oe-date">Date of formation</Label>
                <Input
                  id="oe-date"
                  type="date"
                  value={form.dateOfFormation}
                  onChange={(e) => setForm({ ...form, dateOfFormation: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="oe-ein">EIN</Label>
                {form.id && !editingEin ? (
                  <div className="flex items-center gap-space-2">
                    <span className="font-mono text-small text-gray-mid">
                      {ownedEntities.find((x) => x.id === form.id)?.einMasked ?? "—"}
                    </span>
                    <Button size="sm" variant="secondary" onClick={() => setEditingEin(true)}>
                      Edit EIN
                    </Button>
                  </div>
                ) : (
                  <Input
                    id="oe-ein"
                    value={form.ein}
                    onChange={(e) => setForm({ ...form, ein: e.target.value })}
                    placeholder={form.id ? "Leave blank to clear" : ""}
                  />
                )}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="oe-notes">Notes</Label>
                <Input
                  id="oe-notes"
                  value={form.generalNotes}
                  onChange={(e) => setForm({ ...form, generalNotes: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-space-2">
              <Button size="sm" onClick={save} loading={upsert.isPending}>
                {form.id ? "Save entity" : "Add entity"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setForm(null);
                  setEditingEin(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
